import React from 'react'
import { useLocation } from 'react-router-dom'
import Header from './Header'
import Footer from './Footer'
import AppSidebar from './AppSidebar'
import ThemeToggle from './ThemeToggle'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { Separator } from '@/components/ui/separator'
import { useCustomerAuth } from '../contexts/CustomerAuthContext'
import { useAdminAuth } from '../contexts/AdminAuthContext'

const PAGE_TITLES = [
  { path: '/customer/dashboard', title: 'Dashboard' },
  { path: '/customer/loans', title: 'My Loans' },
  { path: '/customer/financial-data', title: 'Financial Data' },
  { path: '/customer/my-statements', title: 'My Statements' },
  { path: '/admin/dashboard', title: 'Dashboard' },
  { path: '/admin/customers', title: 'Customers' },
  { path: '/admin/financial-statements', title: 'Financial Statements' },
  { path: '/admin/settlements', title: 'Settlements' },
  { path: '/admin/financial-data-entry', title: 'Data Entry' },
  { path: '/admin/product-profiles', title: 'Product Profiles' },
  { path: '/katale/products', title: 'Browse Products' },
  { path: '/katale/cart', title: 'My Cart' },
  { path: '/katale/my-products', title: 'My Products' },
  { path: '/katale/add-product', title: 'Add Product' },
  { path: '/katale/orders', title: 'Orders' },
  { path: '/katale/settlements', title: 'Settlements' },
  { path: '/katale/ratings', title: 'Ratings' },
  { path: '/katale', title: 'MG Katale' },
]

const getPageTitle = (pathname) => {
  const match = PAGE_TITLES.find(
    ({ path }) => pathname === path || pathname.startsWith(`${path}/`)
  )
  return match?.title || 'UFCC Enterprise'
}

const Layout = ({ children }) => {
  const location = useLocation()
  const { isAuthenticated: customerAuth } = useCustomerAuth()
  const { isAuthenticated: adminAuth } = useAdminAuth()

  // SuperAdmin pages have their own layout — no shared header/footer
  const isSuperAdmin = location.pathname.startsWith('/adminsp')

  if (isSuperAdmin) {
    return <>{children}</>
  }

  const isLoginPage =
    location.pathname === '/customer/login' || location.pathname === '/admin/login'

  const isAdminSection = location.pathname.startsWith('/admin')
  const role = isAdminSection ? 'admin' : 'member'
  const hasSidebar =
    !isLoginPage && (isAdminSection ? adminAuth : customerAuth)

  if (!hasSidebar) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1">
          {children}
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <SidebarProvider>
      <AppSidebar role={role} />
      <SidebarInset>
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-sm font-semibold">{getPageTitle(location.pathname)}</h1>
          <ThemeToggle className="ml-auto" />
        </header>
        <div className="flex-1 bg-gray-50">
          {children}
        </div>
        <footer className="border-t bg-background px-4 py-3">
          <p className="text-center text-xs text-muted-foreground">
            © 2025 UFCC Enterprise Monitoring Tool. All rights reserved.
          </p>
        </footer>
      </SidebarInset>
    </SidebarProvider>
  )
}

export default Layout
